import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import { SEGMENT_TRACK, segmentClass } from '../components/ui/tokens';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import AgingReportView from '../features/accountsPayableReports/AgingReportView';
import DueScheduleView from '../features/accountsPayableReports/DueScheduleView';
import SupplierBalancesView from '../features/accountsPayableReports/SupplierBalancesView';

/**
 * Accounts Payable reports (D49 phase 2): aging, due schedule and what we owe each supplier.
 * Reached from the "Reports" button on the Accounts Payable tab. Every figure is summed by the
 * server, each currency in its own table, and every figure links to the bills behind it.
 */

type ReportId = 'aging' | 'due' | 'balances';

const TABS: ReadonlyArray<{ id: ReportId; label: string }> = [
  { id: 'aging', label: 'Aging' },
  { id: 'due', label: 'Due schedule' },
  { id: 'balances', label: 'Supplier balances' },
];

const INFO = (
  <div className="space-y-2">
    <p>These reports show what the company owes its suppliers. They count only bills that still owe money.</p>
    <p>
      Each currency has its own table and is never added to another. Bills on hold or disputed are owed, but
      they are never counted as due. Click a figure to see the bills behind it.
    </p>
  </div>
);

/** The report a link asked for (`?report=due`); anything else opens Aging. */
function initialReport(): ReportId {
  const asked = new URLSearchParams(window.location.search).get('report');
  return TABS.some((tab) => tab.id === asked) ? (asked as ReportId) : 'aging';
}

export const AccountsPayableReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<ReportId>(initialReport);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // The arrow keys move between tabs, the way a tab list is meant to be used from the keyboard.
  const onTabKey = (event: React.KeyboardEvent, index: number) => {
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % TABS.length;
    else if (event.key === 'ArrowLeft') next = (index + TABS.length - 1) % TABS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = TABS.length - 1;
    else return;
    event.preventDefault();
    setActive(TABS[next].id);
    tabRefs.current[TABS[next].id]?.focus();
  };

  return (
    <div className="flex h-full flex-col bg-brand-bg">
      <PageHeader
        title="Accounts Payable reports"
        subtitle="What we owe our suppliers, by how late it is, by when it falls due, and supplier by supplier."
        info={<ModuleInfoButton content={INFO} ariaLabel="Accounts Payable reports information" />}
        back={
          <Button
            variant="secondary"
            icon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />}
            onClick={() => navigate('/accounts?tab=payable')}
          >
            Back
          </Button>
        }
      />

      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
        <div role="tablist" aria-label="Reports" className={`mb-4 ${SEGMENT_TRACK}`}>
          {TABS.map((tab, index) => {
            const selected = tab.id === active;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                id={`ap-report-tab-${tab.id}`}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls={`ap-report-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(tab.id)}
                onKeyDown={(event) => onTabKey(event, index)}
                className={segmentClass(selected)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          role="tabpanel"
          id={`ap-report-panel-${active}`}
          aria-labelledby={`ap-report-tab-${active}`}
        >
          {active === 'aging' ? <AgingReportView /> : null}
          {active === 'due' ? <DueScheduleView /> : null}
          {active === 'balances' ? <SupplierBalancesView /> : null}
        </div>
      </div>
    </div>
  );
};

export default AccountsPayableReportsPage;
